// MeshCore observers (Health page): the payload shape served by GET /api/meshcore/observers and the pure
// filter/sort logic the table renders from it. NO imports on purpose -- compiled on its own and covered by
// node:test (frontend/tests/meshcoreObservers.test.mjs).

export interface McObserver {
  id: string;
  name: string;
  last_heard: string | null;
  rx_1h: number;
  rx_24h: number;
  rx_7d: number;
  region: string;
  region_name: string;
  active: boolean;
}

export interface McObserverRegion {
  code: string;
  name: string;
  count: number;
}

export interface McObserversPayload {
  counts: { active_1h: number; seen_24h: number; seen_7d: number };
  regions: McObserverRegion[];
  observers: McObserver[];
  generated_at?: string;
  pending?: boolean;
  message?: string;
}

export type McSortKey = "name" | "id" | "last_heard" | "rx_1h" | "rx_24h" | "rx_7d" | "region";
export type McSortDir = 1 | -1;

export const MC_ALL_REGIONS = "";

/** Observers in the chosen region (all of them for the empty choice). */
export function filterByRegion(rows: McObserver[], region: string): McObserver[] {
  if (region === MC_ALL_REGIONS) return rows;
  return rows.filter((r) => r.region === region);
}

/** The headline numbers for a set of rows (the page shows them for the chosen region too). */
export function countsFor(rows: McObserver[]): { active_1h: number; seen_24h: number; seen_7d: number } {
  return {
    active_1h: rows.filter((r) => r.active).length,
    seen_24h: rows.filter((r) => r.rx_24h > 0).length,
    seen_7d: rows.length,
  };
}

/** A new sort order. A number or time sorts largest/newest first the first time its column is picked, a text
 * column sorts A to Z first; picking the same column again flips it. */
export function nextSort(current: { key: McSortKey; dir: McSortDir }, key: McSortKey): { key: McSortKey; dir: McSortDir } {
  if (current.key === key) return { key, dir: current.dir === 1 ? -1 : 1 };
  const text = key === "name" || key === "id" || key === "region";
  return { key, dir: text ? 1 : -1 };
}

function cmp(a: McObserver, b: McObserver, key: McSortKey): number {
  switch (key) {
    case "name":
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    case "id":
      return a.id.localeCompare(b.id);
    case "region":
      return a.region_name.localeCompare(b.region_name, undefined, { sensitivity: "base" });
    case "last_heard":
      return (a.last_heard ?? "").localeCompare(b.last_heard ?? "");
    default:
      return a[key] - b[key];
  }
}

/** A sorted copy. Ties fall back to the name, then the id, so the order never jumps between refreshes. */
export function sortObservers(rows: McObserver[], key: McSortKey, dir: McSortDir): McObserver[] {
  return [...rows].sort((a, b) => dir * cmp(a, b, key) || a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) || a.id.localeCompare(b.id));
}
